/**
 * Multi-Range Push-Hook — schickt mehrere Date-Ranges in einem Channex-Call.
 * Vorteil gegenueber per-Range-Calls: weniger Rate-Limit-Druck + atomare Audit-Zeile.
 */
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { toApiError, useApi } from '@/shared/api';
import type { AriPushBatchRequest } from '@/shared/api';
import { addTrackedAriOperations } from './ariOperationStorage';
import {
  isMissingAtChannex,
  isNotRoutable,
  MISSING_AT_CHANNEX,
  NOT_ROUTABLE,
} from './ariPushErrors';

interface UseAriPushBatchArgs {
  onSuccess: () => void;
}

export function useAriPushBatch({ onSuccess }: UseAriPushBatchArgs) {
  const api = useApi();
  return useMutation({
    mutationFn: (body: AriPushBatchRequest) => api.channex.pushAriBatch(body),
    onSuccess: (result) => {
      addTrackedAriOperations(result.propertyId, result.operationIds);
      const ranges = result.rangeCount === 1 ? '1 Zeitraum' : `${result.rangeCount} Zeiträume`;
      const nights = result.totalDays === 1 ? '1 Tag' : `${result.totalDays} Tage`;
      toast.success(`${ranges} (${nights}) vorgemerkt — die Übertragung läuft im Hintergrund`);
      onSuccess();
    },
    onError: async (err) => {
      const e = await toApiError(err);
      const msg = [e.title, e.detail].filter(Boolean).join(' — ');
      if (isNotRoutable(e)) {
        toast.error(NOT_ROUTABLE, { description: msg, duration: 15000 });
      } else if (isMissingAtChannex(e)) {
        toast.error(MISSING_AT_CHANNEX, { description: msg, duration: 15000 });
      } else {
        toast.error(msg || 'Multi-Range-Push fehlgeschlagen.', { duration: 8000 });
      }
    },
  });
}
